import { useEffect, useState, useCallback, useRef } from 'react';
import { useI18n } from '../../i18n/i18n.jsx';
import { useTheme } from '../../hooks/useTheme.jsx';
import ContactFormModal from './ContactFormModal';
import education1 from '../../styles/assets/education/education.jpeg';
import education2 from '../../styles/assets/education/education2.jepg.jpg';
import education3 from '../../styles/assets/education/education3.jepg.jpg';

const EnhancedHero = () => {
  const { t } = useI18n();
  const { isDark } = useTheme();
  const [scrollY, setScrollY] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [nextImage, setNextImage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState(0); // 0 = none, 1 = forward, -1 = backward
  const [nextSlideStart, setNextSlideStart] = useState('100%');
  const intervalRef = useRef(null);

  const images = [education1, education2, education3];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    const next = (currentImage + 1) % images.length;
    setNextImage(next);
    setDirection(1);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImage(next);
      setDirection(0);
      setIsTransitioning(false);
      setNextSlideStart('100%');
    }, 600);
  }, [currentImage, images.length, isTransitioning]);

  const goToPrevious = useCallback(() => {
    if (isTransitioning) return;
    const prev = (currentImage - 1 + images.length) % images.length;
    setNextImage(prev);
    setDirection(-1);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImage(prev);
      setDirection(0);
      setIsTransitioning(false);
      setNextSlideStart('-100%');
    }, 600);
  }, [currentImage, images.length, isTransitioning]);

  const goToSlide = useCallback((index) => {
    if (index === currentImage || isTransitioning) return;
    setNextImage(index);
    const newDirection = index > currentImage ? 1 : -1;
    setDirection(newDirection);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImage(index);
      setDirection(0);
      setIsTransitioning(false);
      setNextSlideStart(newDirection === 1 ? '100%' : '-100%');
    }, 600);
  }, [currentImage, isTransitioning]);

  // Auto-slide every 5 seconds
  useEffect(() => {
    if (isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      goToNext();
    }, 5000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused, goToNext]);

  // Set initial position for next slide when transitioning starts
  useEffect(() => {
    if (isTransitioning && direction !== 0) {
      // Set initial position based on direction
      setNextSlideStart(direction === 1 ? '100%' : '-100%');
      // Force reflow, then animate to center
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setNextSlideStart('0%');
        });
      });
    }
  }, [isTransitioning, direction]);

  // Parallax calculations
  const parallaxOffset = scrollY * 0.5;

  return (
    <section className="relative min-h-[85vh] md:min-h-[85vh] flex items-center justify-center overflow-hidden pt-24">
      {/* Animated Background */}
      <div 
        className="absolute inset-0 overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Current slide */}
        <div
          key={`current-${currentImage}`}
          style={{
            backgroundImage: `url(${images[currentImage]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'absolute',
            inset: 0,
            transform: `translateY(${parallaxOffset * 0.1}px) translateX(${
              isTransitioning && direction !== 0 ? (direction === 1 ? '-100%' : '100%') : '0%'
            })`,
            opacity: isTransitioning && direction !== 0 ? 0 : 1,
            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s ease-in-out',
            zIndex: isTransitioning && direction !== 0 ? 1 : 2
          }}
        />
        
        {/* Next slide - sliding in */}
        {isTransitioning && direction !== 0 && (
          <div
            key={`next-${nextImage}`}
            style={{
              backgroundImage: `url(${images[nextImage]})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'absolute',
              inset: 0,
              transform: `translateY(${parallaxOffset * 0.1}px) translateX(${nextSlideStart})`,
              opacity: 1,
              transition: nextSlideStart === '0%' ? 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
              zIndex: 3
            }}
          />
        )}
        
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/30 dark:bg-black/50 z-[1]" />
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 dark:bg-neutral-800/20 backdrop-blur-md border border-white/30 dark:border-neutral-700/30 flex items-center justify-center text-white hover:bg-white/30 dark:hover:bg-neutral-800/30 transition-all duration-300 hover:scale-110 shadow-lg"
        aria-label="Previous slide"
      >
        <svg
          className="w-5 h-5 md:w-6 md:h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <button
        onClick={goToNext}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 dark:bg-neutral-800/20 backdrop-blur-md border border-white/30 dark:border-neutral-700/30 flex items-center justify-center text-white hover:bg-white/30 dark:hover:bg-neutral-800/30 transition-all duration-300 hover:scale-110 shadow-lg"
        aria-label="Next slide"
      >
        <svg
          className="w-5 h-5 md:w-6 md:h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2 md:gap-3">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
              index === currentImage
                ? 'bg-white w-8 md:w-10 scale-125'
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 text-center mt-20">
        {/* Accent Line with Animation */}
        <div
          className="w-12 md:w-20 h-1 bg-gradient-to-r from-accent to-primary mx-auto mb-4 md:mb-8 rounded-full animate-scale-in"
          style={{ animationDelay: '0.2s' }}
        />

        {/* Main Title with Text Shimmer - Responsive */}
        <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-primary dark:text-white mb-4 md:mb-6 leading-tight animate-fade-in-up">
          <span className="text-shimmer">
            {t('hero.title').split(' ').slice(0, 2).join(' ')}
          </span>
          <br className="hidden md:block" />
          <span className="md:hidden"> </span>
          {t('hero.title').split(' ').slice(2).join(' ')}
        </h1>

        {/* Subtitle - Responsive */}
        <p
          className="text-base sm:text-lg md:text-2xl text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6 md:mb-10 max-w-3xl mx-auto animate-fade-in-up delay-200"
          style={{ animationDelay: '0.4s' }}
        >
          {t('hero.subtitle')}
        </p>

        {/* CTA Buttons - Responsive */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 animate-fade-in-up delay-400"
          style={{ animationDelay: '0.6s' }}
        >
          <button
            onClick={() => setIsModalOpen(true)}
            className="group relative px-6 py-3 md:px-8 md:py-4 bg-primary text-white font-semibold rounded-xl overflow-hidden hover-lift shine-on-hover text-sm md:text-base"
          >
            <span className="relative z-10">{t('hero.cta')}</span>
            <div className="absolute inset-0 bg-primary-light opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>

          <a
            href="/services"
            className="group relative px-6 py-3 md:px-8 md:py-4 bg-transparent border-2 border-primary dark:border-white text-primary dark:text-white font-semibold rounded-xl overflow-hidden hover-lift text-sm md:text-base"
          >
            <span className="relative z-10 group-hover:text-accent transition-colors duration-300">
              {t('hero.learnMore')}
            </span>
            <div className="absolute inset-0 bg-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </a>
        </div>

        {/* Scroll Indicator - Hidden on mobile */}
        
      </div>

      {/* Floating Badge - Responsive */}
      <div className="absolute top-20 md:top-32 right-4 md:right-20 animate-float glass dark:glass-dark rounded-full px-4 py-2 md:px-6 md:py-3 backdrop-blur-sm shadow-xl">
        <span className="text-xs md:text-sm font-semibold text-accent">🎓 {t('stats.successRate.value')} {t('stats.successRate.label')}</span>
      </div>

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </section>
  );
};

export default EnhancedHero;
